/**
 * 模型列表模块级缓存：避免画布上每个生图/生视频配置节点各自请求一次模型列表。
 * 失败后清空缓存，下次调用自动重试。
 *
 * 视频模型另维护一份同步快照（videoModelsSnapshot），供连线手势等
 * 非异步上下文同步查询模型能力（如「该生视频节点是否支持参考视频」）。
 */
import { api, type ModelConfig } from '../../services/api';
import { videoApi, type VideoModelPublicInfo } from '../../baselines/video/services/videoApi';
import { getModelCapabilitiesForMode } from '../../baselines/video/hooks/useVideoModelCapabilities';
import type { VideoMode } from '../../baselines/video/services/videoApi';

let imageModelsPromise: Promise<ModelConfig[]> | null = null;
let videoModelsPromise: Promise<VideoModelPublicInfo[]> | null = null;
let videoModelsSnapshot: VideoModelPublicInfo[] = [];

function refreshVideoModels(): Promise<VideoModelPublicInfo[]> {
  if (!videoModelsPromise) {
    videoModelsPromise = videoApi.getPublicModels().then((res) => {
      videoModelsSnapshot = res;
      return res;
    }).catch((err) => {
      videoModelsPromise = null;
      throw err;
    });
  }
  return videoModelsPromise;
}

export function getCachedImageModels(): Promise<ModelConfig[]> {
  if (!imageModelsPromise) {
    imageModelsPromise = api
      .getModels()
      .then((res) => res.data)
      .catch((err) => {
        imageModelsPromise = null;
        throw err;
      });
  }
  return imageModelsPromise;
}

export function getCachedVideoModels(): Promise<VideoModelPublicInfo[]> {
  return refreshVideoModels();
}

/** 同步查视频模型；未加载时返回 undefined 并触发一次后台加载（下次查询即可命中） */
export function getVideoModelById(modelId: string): VideoModelPublicInfo | undefined {
  const hit = videoModelsSnapshot.find((m) => m.modelId === modelId);
  if (!hit) void refreshVideoModels().catch(() => {});
  return hit;
}

/**
 * 指定模型在指定模式下是否支持参考视频输入。
 * 模型未选择 / 未加载视为不支持（连线手势据此拦截，文案引导用户先选模型）。
 */
export function modelSupportsVideoReference(modelId: string | undefined, mode: VideoMode): boolean {
  if (!modelId) return false;
  const model = getVideoModelById(modelId);
  if (!model) return false;
  return getModelCapabilitiesForMode(model, mode).enableVideoReference;
}

/**
 * 指定模型在指定模式下是否支持参考音频输入。
 * 模型未选择 / 未加载视为不支持（连线手势据此拦截，与参考视频同口径）。
 */
export function modelSupportsAudioReference(modelId: string | undefined, mode: VideoMode): boolean {
  if (!modelId) return false;
  const model = getVideoModelById(modelId);
  if (!model) return false;
  return getModelCapabilitiesForMode(model, mode).enableAudioReference;
}
